import { useGetSrpResultsQuery } from '@/src/api/rtkQuery/srpFlightResults'
import {
    selectFlightsAfterFilterCount,
    selectOnwardFilteredCards,
    selectOnwardRecommendedFilter,
    selectOnwardSort,
    selectOnwardSortedCardIds,
    selectReturnFilteredCards,
    selectReturnRecommendedFilter,
    selectReturnSort,
    selectReturnSortedCardIds,
    selectShowEmptyFlightScreen,
    selectTotalFlights
} from '@/src/redux/selectors/srpSelectors'
import { getRecommendedFilter, getSelectedSort } from '@/src/redux/slices/srpStatesSlice'
import { useAppSelector } from '@/src/redux/store'
import { useMemo } from 'react'
import useGetSearchQueryURL from '../getSearchQueryURL'

/**
 * This hook is used to get the search results from the API with RTK Query.
 * multiple instances of this hook always return updated sync data as it's all states from redux.
 * - call this hooks to get the applied filters and sorted cards.
 *- NOTE: Don't create local states in this hook.

 */
const useSearchResults = (selectedLeg: 0 | 1 = 0) => {
    const { query, error: invalidUrl, isRT, isSolutionView } = useGetSearchQueryURL()
    const {
        data,
        isLoading,
        isFetching,
        error: srpResultsError,
        refetch,
        isUninitialized
    } = useGetSrpResultsQuery(
        { query },
        {
            skip: !query || !!invalidUrl
        }
    )

    const shouldShowMultiAirlinePrices = !!(isRT && isSolutionView)
    const srpResultsLoading = isLoading || isFetching

    const selectedSort = useAppSelector(getSelectedSort)
    const recommendedFilter = useAppSelector(getRecommendedFilter)

    const { cards, sortData, filterData, jsons, sectors, sid, solutionFlowSelectedLeg = 0 } = data || {}

    const selectedJourneyLeg = isSolutionView ? solutionFlowSelectedLeg : selectedLeg

    // Use pre-created selectors based on journey leg - these are shared across all hook instances
    const selectedSortForSelectedJourneyLeg = useAppSelector(
        selectedJourneyLeg === 0 ? selectOnwardSort : selectReturnSort
    )

    const selectedJourneyLegRecommendedFilter = useAppSelector(
        selectedJourneyLeg === 0 ? selectOnwardRecommendedFilter : selectReturnRecommendedFilter
    )

    const selectedSortCardIdsForSelectedJourneyLeg = useAppSelector(state =>
        selectedJourneyLeg === 0 ? selectOnwardSortedCardIds(state, data) : selectReturnSortedCardIds(state, data)
    )

    const selectedLegSortedFilteredCards = useAppSelector(state =>
        selectedJourneyLeg === 0
            ? selectOnwardFilteredCards(state, data, shouldShowMultiAirlinePrices, srpResultsLoading)
            : selectReturnFilteredCards(state, data, shouldShowMultiAirlinePrices, srpResultsLoading)
    )

    const selectedJourneyLegCheckedBaggageIncludedRecommendedFilter =
        selectedJourneyLegRecommendedFilter?.checkedBaggageIncluded || false

    // Memoized calculations for flight counts
    const totalFlights = useMemo(
        () => selectTotalFlights(selectedSortCardIdsForSelectedJourneyLeg),
        [selectedSortCardIdsForSelectedJourneyLeg]
    )

    const numberOfFlightsAfterFilter = useMemo(
        () => selectFlightsAfterFilterCount(selectedLegSortedFilteredCards),
        [selectedLegSortedFilteredCards]
    )

    const showEmptyFlightScreen = useMemo(
        () => selectShowEmptyFlightScreen(selectedLegSortedFilteredCards),
        [selectedLegSortedFilteredCards]
    )

    return {
        srpResultsLoading,
        srpResultsError: srpResultsError as {
            data: Record<string, any>
            status: number
        },
        invalidUrl,
        isSolutionView,
        isRT,
        filterData,
        sortData,
        selectedSortCardIdsForSelectedJourneyLeg,
        selectedSortForSelectedJourneyLeg,
        selectedLegSortedFilteredCards,
        jsons,
        sectors,
        selectedJourneyLeg,
        sid,
        totalFlights,
        numberOfFlightsAfterFilter,
        showEmptyFlightScreen,
        cards,
        selectedSort,
        shouldShowMultiAirlinePrices,
        refetch,
        isUninitialized,
        query
    }
}

export default useSearchResults
